/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [amended INGEST] HOW A FREE-TEXT SOURCE STRING BECOMES A KEY.
 *
 *  The officer ingest matches two columns of somebody else's spreadsheet against two
 *  masters in this database: `SECTION_NM` against the org tree, and `RANK_OF` against
 *  `ranks.code`. Both reductions live HERE, in `packages/shared`, for the reason
 *  `phone.util.ts` does: the CLI runs one of them, `establishment.schema.ts` runs the
 *  same one to prove the alias map is unambiguous, and a later UI import will run both.
 *  Three implementations of "what counts as the same station" is three answers.
 *
 *  ⚠️ NEITHER FUNCTION MAY GROW A SPECIAL CASE FOR A PARTICULAR STATION OR RANK. A
 *  spelling this cannot reach is an ALIAS — a row in `establishment.json` a reviewer can
 *  diff — never a branch in here. That is the same line `establishment.schema.ts` draws
 *  when it refuses to correct "Kolkata Lether Complex": a transform in a script is a rule
 *  nobody can see and nobody can diff.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/**
 * Everything the source uses as a word separator, collapsed to one space.
 *
 * ⚠️ `.` AND `/` ARE DELIBERATELY ABSENT. "Amherst St." and "Bhangar /Bodra" are the
 * establishment's own station names, and "KOL. LEATHER COMPLEX" is the source's own
 * rendering — reducing those punctuation marks would make three different strings look
 * the same and would be a silent merge rather than a match. What bridges them is an
 * ALIAS, which is visible in the file.
 */
const SEPARATORS = /[_\-\s]+/g;

/**
 * ⚠️ A TRAILING `PS` IS STRIPPED; A LEADING OR INTERIOR ONE IS NOT.
 *
 * The source writes both `BOWBAZAR` and `BOWBAZAR PS` for one station, so the suffix
 * carries no information and has to go. It is anchored at the END and it is one token,
 * which is what keeps `PS SECTOR 5` and `POSTA` intact — a bare `.replace(/PS/g, '')`
 * would turn the second into `OTA`.
 */
const TRAILING_PS = / PS$/;

/**
 * The §3b reduction: upper case, separators collapsed, a trailing `PS` removed.
 *
 * ⚠️ IT NEVER RETURNS AN EMPTY STRING FOR A NON-EMPTY INPUT. A section written as
 * exactly `PS` reduces to `PS` rather than to nothing: an empty key would collide with
 * every other blank section in the file and file them all together, which is the one
 * failure mode a resolver must not have. The strip only applies where a word survives it.
 */
export const normaliseSectionName = (raw: string): string => {
  const collapsed = raw.toUpperCase().replace(SEPARATORS, ' ').trim();
  const stripped = collapsed.replace(TRAILING_PS, '').trim();
  return stripped.length > 0 ? stripped : collapsed;
};

/**
 * The letter tokens of a normalised section name.
 *
 * ⚠️ THIS EXISTS SO THAT `DRO` IS TESTED BY MEMBERSHIP AND NEVER BY SUBSTRING, AND THE
 * COST OF GETTING IT WRONG IS `BANSDRONI PS`.
 *
 * `'BANSDRONI'.includes('DRO')` is TRUE. A substring test therefore files South Suburban
 * Division's Bansdroni police station — a real station with real officers — into the
 * divisional reserve office, silently, and every officer in it is then invisible to the
 * station that actually holds them. `tokens('BANSDRONI')` is `['BANSDRONI']`, which does
 * not equal `'DRO'`, and `ingestName.util.test.ts` pins exactly that case.
 *
 * Split on NON-LETTERS rather than on spaces, so `DRO-BD`, `DRO_BD`, `DRO CD` and a bare
 * `DRO` all yield a `DRO` token. `normaliseSectionName` has already collapsed `-` and `_`
 * to spaces by the time this runs; splitting on non-letters as well costs nothing and
 * means this function is still right if it is ever called on a raw value.
 */
export const sectionNameTokens = (normalised: string): string[] =>
  normalised.split(/[^A-Z]+/).filter((token) => token.length > 0);

/** Whether a normalised section name names the divisional reserve office. */
export const namesDro = (normalised: string): boolean => sectionNameTokens(normalised).includes('DRO');

/**
 * The §3c reduction for `RANK_OF` against `ranks.code`: trim, upper case, and collapse
 * INTERNAL whitespace. Nothing else.
 *
 * ⚠️ `/`, `(` AND `)` SURVIVE, AND THAT IS THE WHOLE POINT. `A/ASI`, `P/DR`, `W/SUP(I)`,
 * `ASI(W)` and `ASI(W)(S)` are five distinct rank rows in this database — stripping the
 * punctuation collapses `ASI(W)` and `ASI(W)(S)` onto `ASIWS`-ish keys that merge a
 * Lady Assistant Sub-Inspector (Stenography) with an Assistant Sub-Inspectress. They are
 * different people at different jobs, and nothing downstream could tell they had been
 * merged. `rank.model.ts` already records that a code "reads like a slug and is not one".
 *
 * ⚠️ UPPER CASING IS SAFE HERE AND WOULD NOT BE ON A SECTION NAME'S BEHALF: `ranks.code`
 * carries `uppercase: true` as a mongoose setter, so every stored code is already upper
 * case and this reduction cannot make two of them collide that were not already equal.
 */
export const normaliseRankCode = (raw: string): string => raw.trim().toUpperCase().replace(/\s+/g, ' ');
